class ImageModal {
  constructor() {
    this.modalId = "imageModal";
    this.enlargedImgId = "enlargedImage";
    this.captionId = "imageCaption";
    this.titleId = "imageModalTitle";
    this.hintsClass = "image-modal-hints";

    this.state = {
      scale: 1,
      x: 0,
      y: 0,
      isDragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      initialPinchDist: 0,
      initialScale: 1,
      hasInteracted: false,
    };

    this.init();
  }

  init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const modal = document.getElementById(this.modalId);
    const body = modal?.querySelector(".image-modal-body");
    if (!modal || !body) return;

    body.addEventListener("wheel", (e) => this.handleWheel(e), {
      passive: false,
    });
    body.addEventListener("mousedown", (e) => this.handleMouseDown(e));
    window.addEventListener("mousemove", (e) => this.handleMouseMove(e));
    window.addEventListener("mouseup", () => this.handleMouseUp());

    body.addEventListener("touchstart", (e) => this.handleTouchStart(e), {
      passive: false,
    });
    body.addEventListener("touchmove", (e) => this.handleTouchMove(e), {
      passive: false,
    });
    body.addEventListener("touchend", () => this.handleTouchEnd());

    modal.addEventListener("close", () => this.resetZoom());

    document.querySelectorAll("img:not(a img):not(button img):not(.no-modal)")
      .forEach((i) => {
        if (
          !i.closest("a") && !i.closest("button") && !i.closest(".no-modal")
        ) {
          this.enhanceImage(i);
        }
      });
  }

  handleWheel(e) {
    e.preventDefault();
    this.hideHints();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    this.zoomAt(factor, e.clientX, e.clientY);
  }

  zoomAt(factor, clientX, clientY) {
    const container = document.querySelector(".image-modal-body");
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const nextScale = Math.min(Math.max(this.state.scale * factor, 0.5), 10);

    const mouseX = clientX - (rect.left + rect.width / 2);
    const mouseY = clientY - (rect.top + rect.height / 2);

    const actualFactor = nextScale / this.state.scale;

    this.state.x = mouseX - (mouseX - this.state.x) * actualFactor;
    this.state.y = mouseY - (mouseY - this.state.y) * actualFactor;
    this.state.scale = nextScale;

    this.updateTransform();
  }

  handleMouseDown(e) {
    if (e.button !== 0) return;
    this.hideHints();
    this.state.isDragging = true;
    this.state.lastMouseX = e.clientX;
    this.state.lastMouseY = e.clientY;
  }

  handleMouseMove(e) {
    if (!this.state.isDragging) return;
    this.state.x += e.clientX - this.state.lastMouseX;
    this.state.y += e.clientY - this.state.lastMouseY;
    this.state.lastMouseX = e.clientX;
    this.state.lastMouseY = e.clientY;
    this.updateTransform();
  }

  handleMouseUp() {
    this.state.isDragging = false;
  }

  handleTouchStart(e) {
    this.hideHints();
    if (e.touches.length === 2) {
      e.preventDefault();
      this.state.initialPinchDist = this.getTouchDist(e.touches);
      this.state.initialScale = this.state.scale;
    } else if (e.touches.length === 1) {
      this.state.isDragging = true;
      this.state.lastMouseX = e.touches[0].clientX;
      this.state.lastMouseY = e.touches[0].clientY;
    }
  }

  handleTouchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = this.getTouchDist(e.touches);
      const factor = dist / this.state.initialPinchDist;
      const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      this.zoomAt(
        factor * (this.state.initialScale / this.state.scale),
        centerX,
        centerY,
      );
    } else if (e.touches.length === 1 && this.state.isDragging) {
      this.state.x += e.touches[0].clientX - this.state.lastMouseX;
      this.state.y += e.touches[0].clientY - this.state.lastMouseY;
      this.state.lastMouseX = e.touches[0].clientX;
      this.state.lastMouseY = e.touches[0].clientY;
      this.updateTransform();
    }
  }

  handleTouchEnd() {
    this.state.isDragging = false;
    this.state.initialPinchDist = 0;
  }

  getTouchDist(touches) {
    return Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY,
    );
  }

  updateTransform() {
    const img = document.getElementById(this.enlargedImgId);
    if (img) {
      img.style.transform =
        `translate(${this.state.x}px, ${this.state.y}px) scale(${this.state.scale})`;
    }
  }

  hideHints() {
    if (this.state.hasInteracted) return;
    this.state.hasInteracted = true;
    const hints = document.querySelector(`.${this.hintsClass}`);
    if (hints) hints.classList.add("fade-out");
  }

  resetZoom() {
    this.state = {
      scale: 1,
      x: 0,
      y: 0,
      isDragging: false,
      hasInteracted: false,
    };
    const hints = document.querySelector(`.${this.hintsClass}`);
    if (hints) hints.classList.remove("fade-out");
    this.updateTransform();
  }

  enhanceImage(img) {
    img.classList.add("image-clickable");
    img.setAttribute("role", "button");
    img.setAttribute("aria-label", "View enlarged image");
    img.setAttribute("tabindex", "0");

    const openHandler = () => this.open(img);
    img.addEventListener("click", openHandler);
    img.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openHandler();
      }
    });
  }

  open(img) {
    const enlargedImg = document.getElementById(this.enlargedImgId);
    const caption = document.getElementById(this.captionId);
    const titleElement = document.getElementById(this.titleId);
    if (!enlargedImg) return;

    this.resetZoom();
    enlargedImg.src = img.src;
    enlargedImg.alt = img.alt || "";

    if (caption) {
      let captionText = "";
      const figure = img.closest("figure");
      if (figure) {
        const figcaption = figure.querySelector("figcaption");
        if (figcaption) captionText = figcaption.textContent.trim();
      }
      if (!captionText) captionText = img.alt || img.title || "";

      caption.textContent = captionText;
      caption.style.display = captionText ? "block" : "none";
      if (titleElement) {
        titleElement.textContent = captionText || "Enlarged image";
      }
    }

    if (window.unifiedModals) {
      window.unifiedModals.openSemanticDialog(this.modalId);
    } else {
      const modal = document.getElementById(this.modalId);
      if (modal?.showModal) modal.showModal();
    }
  }
}

window.imageModal = new ImageModal();
